import type { FirebaseApp } from 'firebase/app';
import {
  AppCheckChallengeResult,
  DESKTOP_APP_CHECK_CHALLENGE_FN,
  DESKTOP_APP_CHECK_MINT_FN,
  MintAppCheckTokenResult,
  desktopAppCheckMessage,
  desktopAppCheckRequestMessage,
} from '@koomzo/commonmodel';

import { tillDeviceId } from '../../../register/device-id';
import { WebCryptoDeviceKey, bytesToBase64, indexedDbKeyVault } from '../../offline/device/device-key.port';
import type { DeviceKeyPort } from '../../offline/device/device-key.port';
import { KeptDeviceRegistration, readKeptDeviceRegistration } from '../../offline/device/device-registration-kept';
import { KzAppCheckCoolDown, kzAppCheckFreshWanted, kzAppCheckWithin } from './app-check-wait';

/**
 * App Check on the Electron desktop (kzd-ac AC-3, PLAN-appcheck §4.3; rulings AC-R1, AC-R2). Electron has no
 * attestation provider, so the token is minted by the backend for a REGISTERED device that proves it holds its key:
 * 1. this desktop's registration — the business and device id the device kept from its last answer
 *    (`readKeptDeviceRegistration`, P2-7; it must be `registered` and name THIS device's id, `tillDeviceId()`). None:
 *    no token, and no call is made;
 * 2. `appCheckChallenge({tenantId, deviceId, clientMs, signature})` → `{challengeId, nonce}` — the request itself signed
 *    by the device key over `desktopAppCheckRequestMessage(tenantId, deviceId, clientMs)` (wave-2 review M3: the server
 *    checks it, and the clock within ±2 min, before it issues or charges anything);
 * 3. the device key signs `desktopAppCheckMessage(tenantId, deviceId, nonce)` (`DeviceKeyPort.sign`: ECDSA P-256 /
 *    SHA-256, r‖s — the non-extractable WebCrypto key in IndexedDB);
 * 4. `mintAppCheckToken({challengeId, signature})` → `{token, ttlMillis}` (12 h);
 * 5. `{token, expireTimeMillis: now + ttlMillis}` to the SDK, which keeps it (across restarts too) and renews it.
 *
 * **Why the two calls are a plain `fetch`, not `httpsCallable`:** the Functions SDK asks App Check for a token before
 * it sends any call, and App Check, with no valid token, asks THIS provider — and a token request already on its way
 * is the one every other request waits for (the SDK's `exchangeTokenPromise`). A callable made from inside `getToken`
 * would wait on itself until the call's own timeout. So the two go out by hand, in the callable protocol
 * (`POST https://<region>-<project>.cloudfunctions.net/<name>`, `{data}` in, `{result}` or `{error}` out), with no App
 * Check header (both are exempt) and no ID token (both work signed out — the device key is the proof, not the account,
 * and a desktop at the sign-in page needs a token too). Nothing here touches Auth or the Functions instance, so
 * nothing can wait on App Check. The desktop's CSP already allows it (`connect-src https:`).
 *
 * **Not registered is not a failure** (wave-2 review L1): no call was made, so there is nothing to cool down from —
 * the next request, the moment the desktop is registered, goes out at once.
 *
 * **Fail open**, as the web's source (app-check-wait.ts): every step is bounded (`kzAppCheckWithin`), and a failure is
 * remembered (`KzAppCheckCoolDown`) — not registered (no call), offline, refused, or the server switched off
 * (`desktop-app-check-off`) all end in no token, and the SDK sends without one. A firm refusal (off, a device the server
 * does not allow, a signature it does not take) cools straight to the cap: asking again sooner would get the same.
 */

/** The callables' region (`app.module.ts`'s `getFunctions(undefined, 'europe-west2')`; every `kzOnCall` declares it). */
export const KZ_DESKTOP_APP_CHECK_REGION = 'europe-west2';

/** Refusals that will not change by asking again soon: the cool-down goes straight to its cap. */
const FIRM_REFUSALS: ReadonlySet<string> = new Set(['desktop-app-check-off', 'device-not-allowed', 'bad-signature']);

/** A token as `CustomProvider` takes it (the SDK's `AppCheckToken`). */
export interface KzDesktopAppCheckToken {
  readonly token: string;
  readonly expireTimeMillis: number;
}

/** A call refused (or failed) on the way: the server's `details.code` (else its status), for the cool-down and the log. */
export class KzDesktopAppCheckError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'KzDesktopAppCheckError';
  }
}

/** Posts one callable by hand (see the file comment); a seam for the spec. */
export type KzCallablePost = <T>(name: string, data: unknown) => Promise<T>;

/**
 * The callable protocol over `fetch`, for this app's project: `{data}` in; `{result}` on 200; otherwise `{error:
 * {status, message, details}}`, thrown as a `KzDesktopAppCheckError` whose code is `details.code`, else the status.
 */
export function kzCallablePost(projectId: string, fetchFn: typeof fetch = (input, init) => globalThis.fetch(input, init)): KzCallablePost {
  return async <T>(name: string, data: unknown): Promise<T> => {
    const url = `https://${KZ_DESKTOP_APP_CHECK_REGION}-${projectId}.cloudfunctions.net/${name}`;
    const res = await fetchFn(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data }) });
    const raw: unknown = await res.json().catch(() => null);
    const body = raw && typeof raw === 'object' ? (raw as { result?: T; error?: { status?: string; message?: string; details?: { code?: string } } }) : null;
    if (res.ok && body && 'result' in body) return body.result as T;
    const err = body?.error;
    throw new KzDesktopAppCheckError(err?.message || `${name} answered ${res.status}.`, String(err?.details?.code ?? err?.status ?? `http-${res.status}`));
  };
}

export interface KzDesktopTokenSourceOptions {
  /** The two callables (default: `kzCallablePost` for this app's project). */
  readonly post?: KzCallablePost;
  /** Signs with the device key (default: the WebCrypto key in IndexedDB, the same one `DEVICE_KEY_PORT` holds). */
  readonly keys?: Pick<DeviceKeyPort, 'sign'>;
  /** This desktop's kept registration (default: `readKeptDeviceRegistration`). */
  readonly registration?: () => KeptDeviceRegistration | null;
  /** This device's id (default: `tillDeviceId()`, the id P2-7 registers). */
  readonly deviceId?: () => string;
  /** How long each call may take (`KZ_APP_CHECK_WAIT_MS`). */
  readonly waitMs: number;
  /** The clock, for the spec. */
  readonly now?: () => number;
}

/**
 * The desktop's token source behind its `CustomProvider` (see the file comment). One request at a time: a second one
 * while the first is on its way waits for the same answer — except inside a fresh window (`kzAppCheckFreshWanted`,
 * AC-5: a limited-use call that the server consumes): such a request runs its own challenge and mint, never shares
 * one on its way, and is not shared either. Every mint is a new token, so a limited-use call never sends one already
 * sent.
 */
export function kzDesktopTokenSource(app: FirebaseApp, opts: KzDesktopTokenSourceOptions): () => Promise<KzDesktopAppCheckToken> {
  const now = opts.now ?? (() => Date.now());
  const cool = new KzAppCheckCoolDown(now);
  const registration = opts.registration ?? (() => readKeptDeviceRegistration());
  const thisDevice = opts.deviceId ?? tillDeviceId;
  let post = opts.post ?? null;
  let keys = opts.keys ?? null;
  let inflight: Promise<KzDesktopAppCheckToken> | null = null;

  const ask = async (): Promise<KzDesktopAppCheckToken> => {
    const reg = registration();
    if (!reg || reg.state !== 'registered' || reg.deviceId !== thisDevice()) {
      throw new KzDesktopAppCheckError('App Check: this desktop is not a registered device; no token.', 'not-registered');
    }
    post ??= kzCallablePost(String(app.options.projectId ?? ''));
    keys ??= new WebCryptoDeviceKey(indexedDbKeyVault());
    const { tenantId, deviceId } = reg;
    try {
      const clientMs = Math.round(now());
      const request = bytesToBase64(await keys.sign(deviceId, new TextEncoder().encode(desktopAppCheckRequestMessage(tenantId, deviceId, clientMs))));
      const challenge = await kzAppCheckWithin(
        post<AppCheckChallengeResult>(DESKTOP_APP_CHECK_CHALLENGE_FN, { tenantId, deviceId, clientMs, signature: request }),
        opts.waitMs,
        'App Check: the desktop challenge did not come in time.',
      );
      const message = new TextEncoder().encode(desktopAppCheckMessage(tenantId, deviceId, String(challenge?.nonce ?? '')));
      const signature = bytesToBase64(await keys.sign(deviceId, message));
      const minted = await kzAppCheckWithin(
        post<MintAppCheckTokenResult>(DESKTOP_APP_CHECK_MINT_FN, { challengeId: challenge?.challengeId, signature }),
        opts.waitMs,
        'App Check: the desktop token did not come in time.',
      );
      if (typeof minted?.token !== 'string' || !minted.token || !(typeof minted.ttlMillis === 'number' && minted.ttlMillis > 0)) {
        throw new KzDesktopAppCheckError('App Check: the desktop token came back malformed.', 'malformed');
      }
      cool.reset();
      return { token: minted.token, expireTimeMillis: now() + minted.ttlMillis };
    } catch (err) {
      cool.fail(FIRM_REFUSALS.has((err as { code?: string } | null)?.code ?? ''));
      throw err;
    }
  };

  return () => {
    const fresh = kzAppCheckFreshWanted();
    if (inflight && !fresh) return inflight;
    if (cool.cooling) return Promise.reject(new Error('App Check: the desktop token failed a moment ago; not asked again yet.'));
    const p = ask();
    if (fresh) return p;
    inflight = p;
    const clear = () => {
      if (inflight === p) inflight = null;
    };
    p.then(clear, clear);
    return p;
  };
}
